// moduleImportBridge.js — capability-scoped VENDOR-LIBRARY loading for
// workspace MODULES (manifest v2). The third capability bridge, alongside
// moduleDataBridge (data) and moduleFileBridge (uploads).
//
// ── Why this exists ────────────────────────────────────────────────────────
// A catalog app that wraps a third-party library (a drawing canvas, a chart
// pack, a video player) has to get that library into the page. Until now it
// could NOT:
//   • `import('pkg')` inside module code is FORBIDDEN — ModuleAuditService's
//     FORBIDDEN_PATTERNS bans dynamic import(), and a version whose
//     `moduleAudit.ok === false` is REFUSED outright by the loader.
//   • The module loader has no importmap support, so a bare specifier has
//     nothing to resolve against either.
// So the framework, not the app, must own the import — exactly the shape the
// data plane already uses. The `import()` below lives in SHELL code, which is
// not module-audited; modules stay audit-clean because they only ever call
// `el.call('moduleImport', pkg)`.
//
// ── Trust model (identical to moduleData) ──────────────────────────────────
// The allowlist is the module's own manifest `libraries` map, which the
// workspace owner/admin sees at install time. A module can load NOTHING it did
// not declare: an undeclared package throws, and the declared version is the
// one fetched — the module never supplies a URL, so it cannot point the shell
// at arbitrary code. Every request is pinned to an exact version (a floating
// range would let an upstream release change what an installed app executes).
//
// ── Why esm.sh, and why NOT (yet) pkg.symbo.ls ─────────────────────────────
// Measured in a browser, not assumed (scratchpad mount harness, 2026-08-08):
// our own mirror is jsDelivr-backed, and jsDelivr's `+esm` bundles resolve
// each package's peers INDEPENDENTLY — Excalidraw's bundle pulled react@19.2.5
// while its own `jotai-scope` dep pulled react@18.3.1. Two React copies means a
// null hooks dispatcher ("Cannot read properties of null (reading 'useEffect')")
// and a silent blank mount. No amount of pinning OUR react fixes a peer the
// bundle resolved itself.
//
// esm.sh's `?deps=` pins peers across the WHOLE transitive graph — the pins are
// encoded into every nested URL it emits — so one React instance reaches every
// package. Verified: the editor mounts and paints.
//
// Moving this onto pkg.symbo.ls is tracked in tickets/excalidraw.md: the worker
// must mirror esm.sh (whose nested imports are root-relative, e.g.
// `/react@19.2.5/es2022/react.mjs`), which needs a routing decision — a
// dedicated host, or a path heuristic — that is operator territory. `CDN_BASE`
// is the single line that changes when that lands.
//
// Either way NOTHING is added to the workspace bundle: the shell ships this
// resolver and nothing else; library bytes arrive only when a user opens an
// installed app that asks for them.

const CDN_BASE = 'https://esm.sh'

// Exact-version pin. A range/tag would mean an upstream publish silently
// changes what an already-installed app runs.
// Raw file mirror for non-module assets (css, fonts, workers). Our own CDN
// handles these correctly today — only the ESM *graph* needed esm.sh.
const ASSET_BASE = 'https://pkg.symbo.ls'

const EXACT_VERSION = /^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?$/

// url → Promise<namespace>. Module-scope so two widgets mounting the same
// library in one tick share ONE network fetch (same reasoning as the records
// stampede collapse in moduleDataBridge).
const _moduleCache = new Map()
// url → Promise<parsed JSON>. A separate cache from `_moduleCache`: a JSON
// data file and an ES module are different fetch kinds, even for the same
// declared package, and must not collide on the same key.
const _jsonCache = new Map()
const _cssLoaded = new Set()

/**
 * URL for a MODULE import — the entry, or a deep subpath. Always `+esm`:
 * that suffix is what converts CJS to ESM, and a subpath needs it just as
 * much as the entry (`react-dom/client` without it serves CJS and the browser
 * refuses the import — caught by the mount harness, not by unit tests).
 * Pure — no network. Exported for tests.
 *
 *   ('react', '18.3.1')                    → …/react@18.3.1/+esm
 *   ('react-dom', '18.3.1', 'client')      → …/react-dom@18.3.1/client/+esm
 */
export const resolveModuleUrl = (pkg, version, path, pins) => {
  const base = `${CDN_BASE}/${pkg}@${version}`
  const sub = path ? '/' + String(path).replace(/^\/+/, '').replace(/\/+$/, '') : ''
  // `?deps=` is what makes the transitive graph agree on ONE copy of a shared
  // peer (React). Built from the app's declared map — never hardcoded.
  const q = pins && pins.length ? `?deps=${pins.join(',')}` : ''
  return `${base}${sub}${q}`
}

/**
 * URL for an ASSET inside a package — stylesheets, fonts, workers. Served
 * VERBATIM: `+esm` here would try to parse a `.css`/`.woff2` as a module.
 *
 *   ('@excalidraw/excalidraw', '0.18.1', 'dist/prod/index.css')
 *     → …/@excalidraw/excalidraw@0.18.1/dist/prod/index.css
 *   ('@excalidraw/excalidraw', '0.18.1')  → …/@excalidraw/excalidraw@0.18.1/
 */
export const resolveAssetUrl = (pkg, version, path) => {
  // Assets bypass the ESM builder entirely — a raw file mirror serves them.
  const base = `${ASSET_BASE}/${pkg}@${version}`
  if (!path) return `${base}/`
  return `${base}/${String(path).replace(/^\/+/, '')}`
}

/**
 * Normalize a manifest `libraries` map into an allowlist.
 * Accepts `{ 'pkg': '1.2.3' }`. Entries with a missing or non-exact version
 * are DROPPED with a warning rather than silently floating.
 */
export const normalizeDependencies = (declared) => {
  const allow = new Map()
  if (!declared || typeof declared !== 'object') return allow
  for (const [pkg, version] of Object.entries(declared)) {
    if (typeof pkg !== 'string' || !pkg) continue
    const v = typeof version === 'string' ? version.trim().replace(/^[\^~]/, '') : ''
    if (!EXACT_VERSION.test(v)) {
      console.warn(
        '[moduleImport] dependency skipped — an EXACT version is required (got',
        JSON.stringify(version),
        'for',
        pkg + ')'
      )
      continue
    }
    allow.set(pkg, v)
  }
  return allow
}

/**
 * Inject a stylesheet once per URL. Vendor CSS (e.g. Excalidraw's) is
 * namespaced by the library itself; the shell does not sandbox it.
 */
const _shellInjectCss = (url) => {
  if (_cssLoaded.has(url) || typeof document === 'undefined') return
  _cssLoaded.add(url)
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = url
  link.dataset.moduleImport = 'true'
  document.head.appendChild(link)
}

/**
 * Build the bridge the loader registers as the `moduleImport` context
 * function. Modules call:
 *
 *   const Lib = await el.call('moduleImport', '@excalidraw/excalidraw')
 *   const Lib = await el.call('moduleImport', 'react-dom', { path: 'client' })
 *   await el.call('moduleImport', '@excalidraw/excalidraw', {
 *     css: 'dist/prod/index.css'
 *   })
 *   const topo = await el.call('moduleImport', 'world-atlas', {
 *     path: 'land-110m.json', json: true
 *   })
 *
 * `opts.json: true` fetches a DATA file off the same asset resolver as
 * `opts.css` (declared package + exact version, never a module URL) and
 * returns the parsed JSON — for a library data file (TopoJSON, a lookup
 * table) that is not and never becomes an ES module. Without it, the only
 * way to pull a JSON file through this bridge was a CDN-specific query flag
 * smuggled into `opts.path` (`?module`), which depends on esm.sh and breaks
 * if the resolver changes host or drops the flag.
 *
 * Returns the module namespace, or the parsed JSON for `opts.json`. Throws
 * (never resolves undefined) when the package isn't declared — a silent
 * null would surface as an unrenderable component with no explanation.
 */
// Peer packages worth pinning across a dependency graph. Anything the app
// declares that also appears as a nested peer must resolve to ONE copy.
const PINNABLE_PEERS = ['react', 'react-dom', 'preact', 'vue', 'svelte']

export const peerPins = (allow) =>
  PINNABLE_PEERS.filter((p) => allow.has(p)).map((p) => `${p}@${allow.get(p)}`)

export const buildModuleImportBridge = (declaredDependencies, options = {}) => {
  const allow = normalizeDependencies(declaredDependencies)
  const pins = peerPins(allow)
  // Injectable for tests — the real ones are the platform's dynamic import
  // and a plain fetch-and-parse.
  const importFn = options.importFn || ((url) => import(/* @vite-ignore */ url))
  const fetchJsonFn =
    options.fetchJsonFn ||
    (async (url) => {
      const res = await fetch(url)
      if (!res.ok) throw new Error(`[moduleImport] ${res.status} fetching ${url}`)
      return res.json()
    })

  return async function moduleImport(pkg, opts = {}) {
    if (typeof pkg !== 'string' || !pkg) {
      throw new Error('[moduleImport] a package name is required')
    }
    const version = allow.get(pkg)
    if (!version) {
      throw new Error(
        `[moduleImport] "${pkg}" is not declared in this app's manifest \`libraries\` — ` +
          'declare it with an exact version to make it loadable.'
      )
    }

    if (opts.css) _shellInjectCss(resolveAssetUrl(pkg, version, opts.css))

    if (opts.json) {
      // Same resolver as `opts.css` — a raw file, never the ESM builder.
      const url = resolveAssetUrl(pkg, version, opts.path)
      if (!_jsonCache.has(url)) {
        _jsonCache.set(
          url,
          fetchJsonFn(url).catch((err) => {
            // Don't poison the cache with a transient network failure — the
            // next call should be able to retry.
            _jsonCache.delete(url)
            throw err
          })
        )
      }
      return _jsonCache.get(url)
    }

    // `assetPath` — some libraries need a base URL to fetch their own runtime
    // assets (fonts, workers) from. Returning it lets the app hand the library
    // a pkg.symbo.ls path instead of hardcoding a CDN.
    const url = resolveModuleUrl(pkg, version, opts.path, pins)
    if (!_moduleCache.has(url)) {
      _moduleCache.set(
        url,
        importFn(url).catch((err) => {
          // Don't poison the cache with a transient network failure — the next
          // mount should be able to retry.
          _moduleCache.delete(url)
          throw err
        })
      )
    }
    return _moduleCache.get(url)
  }
}

// node → mounted root, and node → the library's imperative API. WeakMaps so a
// discarded node's handles are collectable without the app tracking anything.
const _mountedRoots = new WeakMap()
const _mountedApis = new WeakMap()
const _injectedSheets = new Set()

/**
 * Mount a vendor REACT component into an element's node — the whole job, in
 * shell code.
 *
 * Apps must not do this themselves: writing `el.node.*` is banned (FA511),
 * assigning to `window` is banned (FA409), and both are exactly what a React
 * mount needs (a root handle to stash, and — for libraries like Excalidraw —
 * a global telling them where to fetch their own fonts). Owning it here keeps
 * app code audit-clean AND means the next vendor-UI app is one call, not a
 * hand-rolled mount.
 *
 *   await el.call('moduleMount', el, {
 *     pkg: '@excalidraw/excalidraw',
 *     export: 'Excalidraw',
 *     css: 'dist/prod/index.css',
 *     assetPath: 'dist/prod/', assetPathGlobal: 'EXCALIDRAW_ASSET_PATH',
 *     componentProps: { … }
 *   })
 *
 * Idempotent: a node that already holds a root is left alone, so a DOMQL
 * re-render can never stack two React trees on one node.
 */
export const buildModuleMountBridge = (declaredDependencies, options = {}) => {
  const moduleImport = buildModuleImportBridge(declaredDependencies, options)
  const moduleAssetUrl = buildModuleAssetUrlBridge(declaredDependencies)

  return async function moduleMount(el, spec = {}) {
    const node = el && el.node
    if (!node) return null
    if (_mountedRoots.has(node)) return _mountedRoots.get(node)
    if (!spec.pkg) throw new Error('[moduleMount] `pkg` is required')

    // Set BEFORE the library evaluates — asset globals are read at module init.
    if (spec.assetPathGlobal) {
      globalThis[spec.assetPathGlobal] = moduleAssetUrl(spec.pkg, spec.assetPath || '')
    }

    const React = await moduleImport('react')
    const ReactDOMClient = await moduleImport('react-dom', { path: 'client' })
    const Lib = await moduleImport(spec.pkg, spec.css ? { css: spec.css } : undefined)

    const Component = spec.export ? Lib[spec.export] : Lib.default || Lib
    if (typeof Component !== 'function' && typeof Component !== 'object') {
      throw new Error(
        `[moduleMount] "${spec.export || 'default'}" is not a component export of ${spec.pkg}`
      )
    }

    // Brand skin: the app hands us CSS custom properties to set on the
    // mounted subtree, so a vendor UI can be dressed in OUR design-system
    // tokens (`var(--color-…)`) without the app writing to the DOM itself.
    //
    // `cssVarsTarget` matters more than it looks. A library that declares its
    // own theme vars ON ITS ROOT (`.excalidraw { --island-bg-color: … }`)
    // OVERRIDES anything inherited from an ancestor — setting the vars on the
    // mount node alone silently did nothing (measured: islands kept
    // Excalidraw's #232329 instead of the workspace surface). So the vars are
    // applied to the library's own root too, once React has rendered it.
    const applyVars = (target) => {
      if (!target || !spec.cssVars || typeof spec.cssVars !== 'object') return
      for (const [name, value] of Object.entries(spec.cssVars)) {
        if (typeof name === 'string' && name.startsWith('--')) target.style.setProperty(name, value)
      }
    }
    applyVars(node)

    // Scoped stylesheet. Custom properties cover most vendor skinning, but not
    // everything a house surface needs — `backdrop-filter` and a layered
    // `box-shadow` are real declarations, not variables any library exposes.
    // The app supplies CSS text (referencing OUR theme vars, so it stays
    // token-driven and theme-aware) and the shell injects it once.
    if (typeof spec.styleSheet === 'string' && spec.styleSheet.trim()) {
      const id = 'module-skin-' + String(spec.pkg).replace(/[^a-z0-9]+/gi, '-')
      if (!_injectedSheets.has(id) && typeof document !== 'undefined') {
        _injectedSheets.add(id)
        const style = document.createElement('style')
        style.id = id
        style.textContent = spec.styleSheet
        document.head.appendChild(style)
      }
    }
    if (spec.cssVars && spec.cssVarsTarget) {
      // React renders asynchronously — poll briefly for the root rather than
      // guessing a timeout, and give up quietly if the selector never matches.
      let tries = 0
      const paint = () => {
        const found = node.querySelector(spec.cssVarsTarget)
        if (found) return applyVars(found)
        if (tries++ < 40) setTimeout(paint, 75)
      }
      setTimeout(paint, 0)
    }

    // Imperative API handle. Libraries like Excalidraw hand back a controller
    // through a prop (`excalidrawAPI`); the app cannot stash it itself (no
    // el.node writes), so the shell captures it into the same WeakMap and
    // serves it via `el.call('moduleApi', el)`. This is what lets an app
    // drive the mounted library AFTER mount — live theme changes, AI-authored
    // content — without ever re-mounting it.
    const props = { ...(spec.componentProps || {}) }
    if (spec.apiProp) {
      const userHook = props[spec.apiProp]
      props[spec.apiProp] = (api) => {
        _mountedApis.set(node, api)
        if (typeof userHook === 'function') userHook(api)
      }
    }

    const root = ReactDOMClient.createRoot(node)
    _mountedRoots.set(node, root)
    root.render(React.createElement(Component, props))
    return root
  }
}

/**
 * The imperative API a `moduleMount`ed library handed back
 * (`el.call('moduleApi', el)`), or null before it is ready. Captured by the
 * mount's `apiProp` hook — see buildModuleMountBridge.
 */
export const buildModuleApiBridge = () =>
  function moduleApi(el) {
    const node = el && el.node
    if (!node) return null
    return _mountedApis.get(node) || null
  }

/**
 * Resolve a design-system token to its literal value
 * (`el.call('moduleToken', '--color-background')`).
 *
 * Needed because some vendor surfaces are PAINTED, not styled: Excalidraw
 * draws its canvas ground onto a <canvas>, where `var(--color-…)` means
 * nothing. Resolving here keeps app code free of raw DOM reads and keeps the
 * value sourced from the live theme rather than a copied literal.
 */
export const buildModuleTokenBridge = () =>
  function moduleToken(name, fallback) {
    if (typeof name !== 'string' || !name.startsWith('--')) return fallback || null
    if (typeof document === 'undefined' || typeof window === 'undefined') return fallback || null
    const value = window.getComputedStyle(document.documentElement).getPropertyValue(name)
    const trimmed = (value || '').trim()
    return trimmed || fallback || null
  }

/**
 * The RESOLVED global theme — `'dark' | 'light'` — as the shell currently
 * applies it (`el.call('moduleTheme')`).
 *
 * Needed because nothing module-visible carries the RESOLUTION: an element's
 * `context.globalTheme` is the boot-time MODE and is never rewritten (under
 * `'auto'` it stays `'auto'` forever — scratch's `changeGlobalTheme` mutates
 * its own config + the document attribute, not element contexts), and root
 * state's `themeMode` is also the mode. Only the shell's `data-theme`
 * attribute knows what `'auto'` resolved to. Painted surfaces (Excalidraw's
 * canvas ground) need the resolved value to re-theme — same reasoning as
 * `moduleToken` above: resolve here so app code stays free of raw DOM reads.
 */
export const buildModuleThemeBridge = () =>
  function moduleTheme() {
    if (typeof document === 'undefined') return 'light'
    const attr = document.documentElement.getAttribute('data-theme')
    if (attr === 'dark' || attr === 'light') return attr
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return 'light'
  }

/**
 * Tear down a `moduleMount`ed tree (`el.call('moduleUnmount', el)`). A React
 * root left mounted on a removed node keeps its listeners and rAF loop alive.
 */
export const buildModuleUnmountBridge = () =>
  function moduleUnmount(el) {
    const node = el && el.node
    if (!node) return false
    const root = _mountedRoots.get(node)
    _mountedApis.delete(node)
    if (!root) return false
    _mountedRoots.delete(node)
    try {
      root.unmount()
    } catch (err) {
      console.warn('[moduleUnmount] unmount failed:', err)
    }
    return true
  }

/**
 * Companion bridge: resolve an ASSET url inside a declared dependency without
 * importing it (`el.call('moduleAssetUrl', pkg, 'dist/prod/')`). Libraries
 * that self-fetch fonts/workers need this as a base path.
 */
export const buildModuleAssetUrlBridge = (declaredDependencies) => {
  const allow = normalizeDependencies(declaredDependencies)
  return function moduleAssetUrl(pkg, path) {
    const version = allow.get(pkg)
    if (!version) {
      throw new Error(
        `[moduleAssetUrl] "${pkg}" is not declared in this app's manifest \`libraries\`.`
      )
    }
    // Assets are served verbatim; no path yields the package ROOT with a
    // trailing slash (a base path a library appends to).
    return resolveAssetUrl(pkg, version, path)
  }
}
